import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./SpecialOffers.css";
import api from "../../../api/axios"; 

const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  function calculateTimeLeft() {
    const difference = +new Date(targetDate) - +new Date();
    let timeLeft = {};
    if (difference > 0) {
      timeLeft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        mins: Math.floor((difference / 1000 / 60) % 60),
        secs: Math.floor((difference / 1000) % 60),
      };
    }
    return timeLeft;
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearTimeout(timer);
  });

  const timerComponents = [];
  Object.keys(timeLeft).forEach((interval) => {
    if (!timeLeft[interval] && timeLeft[interval] !== 0) return;
    timerComponents.push(
      <div className="so-timer-box" key={interval}>
        <span className="so-timer-value">{timeLeft[interval]}</span>
        <span className="so-timer-label">{interval.toUpperCase()}</span>
      </div>
    );
  });

  return (
    <div className="so-countdown-container">
      {timerComponents.length ? timerComponents : <span>Offer Expired!</span>}
    </div>
  );
};

const SpecialOffers = () => {
  const [allOffers, setAllOffers] = useState([]);
  const [visibleCount, setVisibleCount] = useState(3); 
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate(); 

  const getImageUrl = (property) => {
    if (!property.photos || property.photos.length === 0) {
      return "https://via.placeholder.com/800x600?text=Exclusive+Offer";
    }
    const photo = property.photos[0];
    return photo.file_path.startsWith("http") ? photo.file_path : `${api.defaults.baseURL}/${photo.file_path}`; 
  };

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const response = await api.get("/api/get-properties");
        if (response.data && response.data.data) {
          const formatted = response.data.data.map((item, index) => ({
            id: item.id,
            title: item.name,
            subtitle: item.location ? `${item.location.area}, ${item.location.city}` : "Premium Living",
            description: item.description || "Experience luxury living with exclusive amenities and limited-time pricing benefits.",
            image: getImageUrl(item),
            discount: index % 3 === 0 ? "15% OFF" : (index % 3 === 1 ? "Zero Fees" : "2 Months Free"),
            tag: index % 3 === 0 ? "LIMITED TIME" : (index % 3 === 1 ? "POPULAR" : "BUSINESS"),
            endDate: new Date(Date.now() + 86400000 * (index + 2)), 
          }));
          setAllOffers(formatted);
        }
      } catch (error) { console.error(error); } finally { setLoading(false); }
    };
    fetchOffers();
  }, []);

  const handleSeeMore = () => {
    setVisibleCount(prev => prev + 3); 
  };

  if (loading || allOffers.length === 0) return null;

  const firstThree = allOffers.slice(0, 3);
  const largeOffer = firstThree[0];
  const stackedOffers = firstThree.slice(1);
  const additionalOffers = allOffers.slice(3, visibleCount);

  return (
    <section className="so-section">
      <div className="so-container">
        <div className="so-header">
          <h2>EXCLUSIVE <span className="so-highlight">DEALS</span></h2>
          <p>Unbeatable offers on premium properties selected just for you</p>
        </div>

        {/* Top Layout: 1 Large Card + 2 Stacked Small Cards */}
        <div className="so-main-grid">
          {largeOffer && (
            <div className="so-card so-large-card">
              <div className="so-bg" style={{ backgroundImage: `url(${largeOffer.image})` }}></div>
              <div className="so-overlay"></div>
              <div className="so-content">
                <div className="so-tag">{largeOffer.tag}</div>
                <h3 className="so-title">{largeOffer.title}</h3>
                <p className="so-subtitle">{largeOffer.subtitle}</p>
                <div className="so-discount-large">{largeOffer.discount}</div>
                <p className="so-desc">{largeOffer.description.substring(0, 120)}...</p>
                <div className="so-timer-wrapper">
                  <p>OFFER ENDS IN:</p>
                  <CountdownTimer targetDate={largeOffer.endDate} />
                </div>
                <button className="so-claim-btn" onClick={() => navigate(`/greenland/properties/${largeOffer.id}`)}>
                  View Details
                </button>
              </div>
            </div>
          )}

          <div className="so-stack">
            {stackedOffers.map(offer => (
              <div key={offer.id} className="so-card so-small-card">
                <div className="so-small-img-box">
                  <img src={offer.image} alt="" />
                  <div className="so-small-badge">{offer.discount}</div>
                </div>
                <div className="so-small-content">
                  <span className="so-category">{offer.tag}</span>
                  <h4>{offer.title}</h4>
                  <p>{offer.description.substring(0, 60)}...</p>
                  <button className="so-text-link" onClick={() => navigate(`/greenland/properties/${offer.id}`)}>
                    View Details →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Cards: Row-wise Grid */}
        {additionalOffers.length > 0 && (
          <div className="so-row-grid">
            {additionalOffers.map(offer => (
              <div key={offer.id} className="so-card so-row-item">
                <div className="so-row-img-box">
                  <img src={offer.image} alt="" />
                  <div className="so-small-badge">{offer.discount}</div>
                </div>
                <div className="so-small-content">
                  <span className="so-category">{offer.tag}</span>
                  <h4>{offer.title}</h4>
                  <p>{offer.description.substring(0, 60)}...</p>
                  <button className="so-text-link" onClick={() => navigate(`/greenland/properties/${offer.id}`)}>
                    View Details →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* See More Button */}
        {visibleCount < allOffers.length && (
          <div className="so-see-more-wrapper">
            <button className="so-see-more-btn" onClick={handleSeeMore}>
              See All Properties
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default SpecialOffers;